import React, { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronDown, Eye, Save, X } from "lucide-react";
import AdminLayout from "./AdminLayout";
import AdminUserLink from "../components/AdminUserLink";
import API from "../services/api";
import { getMarketWindowForNow } from "../utils/marketStatus";

const DEFAULT_RESULT_SETTINGS = {
  market_open_result_reveal_minutes: "30",
  market_outcome_reveal_minutes: "10",
  market_upcoming_result_window_minutes: "30",
  market_pending_result_delay_minutes: "10",
  market_day_market_cutoff_hour: "18",
  market_result_day_reset_hour: "2",
  market_next_day_bet_start_hour: "0",
};

function AdminResults() {
  const defaultResultDateRef = useRef(getBusinessResultDate());
  const [markets, setMarkets] = useState([]);
  const [results, setResults] = useState([]);
  const [validPannas, setValidPannas] = useState([]);
  const [resultSettings, setResultSettings] = useState(DEFAULT_RESULT_SETTINGS);
  const [preview, setPreview] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingFlags, setPendingFlags] = useState([]);
  const [marketMenuOpen, setMarketMenuOpen] = useState(false);
  const [panaMenuOpen, setPanaMenuOpen] = useState(false);
  const [form, setForm] = useState({
    result_date: defaultResultDateRef.current,
    market_id: "",
    session: "OPEN",
    pana: "",
    digit: "",
  });

  const openRevealMinutes = clampInteger(resultSettings.market_open_result_reveal_minutes, 30, 0, 1440);
  const closeRevealMinutes = clampInteger(resultSettings.market_outcome_reveal_minutes, 10, 0, 1440);
  const upcomingWindowMinutes = clampInteger(resultSettings.market_upcoming_result_window_minutes, 30, 0, 1440);
  const dayMarketCutoffHour = clampInteger(resultSettings.market_day_market_cutoff_hour, 18, 0, 23);
  const resultRowsForDate = results.filter((item) => String(item.result_date || "").slice(0, 10) === form.result_date);
  const declaredSessions = getDeclaredSessions(resultRowsForDate, form.market_id);
  const availabilitySettings = {
    openRevealMinutes,
    closeRevealMinutes,
    dayMarketCutoffHour,
  };
  const availableMarkets = markets.filter((market) => isMarketAvailableForResult(market, form.result_date, resultRowsForDate, availabilitySettings));
  const upcomingResults = getUpcomingResultItems(markets, form.result_date, resultRowsForDate, {
    ...availabilitySettings,
    upcomingWindowMinutes,
  });
  const sortedResults = sortDeclaredResults(results);
  const selectedMarket = availableMarkets.find((item) => String(item.id) === String(form.market_id));
  const marketMenuRef = useRef(null);
  const panaMenuRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const panaSuggestions = getPanaSuggestions(form.pana, validPannas);
  const openDeclared = declaredSessions.open;
  const closeDeclared = declaredSessions.close;
  const overdueState = getOverdueState(selectedMarket, form.result_date, form.session, declaredSessions);
  const pendingKey = selectedMarket ? `${selectedMarket.id}-${form.result_date}-${form.session}` : "";
  const pendingRow = pendingFlags.find((item) => `${item.market_id}-${String(item.result_date).slice(0, 10)}-${String(item.session || "").toUpperCase()}` === pendingKey);

  useEffect(() => {
    const run = async () => {
      await Promise.all([loadValidPannas(), loadMarkets(), loadResults(), loadResultSettings()]);
    };
    run();
  }, []);

  useEffect(() => () => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
  }, []);

  useEffect(() => {
    const handleClick = (event) => {
      if (marketMenuRef.current && !marketMenuRef.current.contains(event.target)) {
        setMarketMenuOpen(false);
      }
      if (panaMenuRef.current && !panaMenuRef.current.contains(event.target)) {
        setPanaMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const loadMarkets = async () => {
    try {
      const res = await API.get("/admin/markets");
      const data = Array.isArray(res.data)
        ? res.data.filter((item) => (item.market_type || "normal") !== "starline")
            .sort((a, b) => String(a.open_time || "").localeCompare(String(b.open_time || "")))
        : [];
      setMarkets(data);
    } catch {
      setMarkets([]);
    }
  };

  const loadValidPannas = async () => {
    try {
      const res = await API.get("/admin/valid-pannas");
      const values = Array.isArray(res.data) ? res.data.map((item) => String(item.pana || "")) : [];
      setValidPannas(values.filter(Boolean));
    } catch {
      setValidPannas([]);
    }
  };

  const loadResults = async () => {
    try {
      const res = await API.get("/admin/results");
      setResults(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setPendingFlags(Array.isArray(res.data?.pending_flags) ? res.data.pending_flags : []);
    } catch {
      setResults([]);
      setPendingFlags([]);
    }
  };

  const loadResultSettings = async () => {
    try {
      const res = await API.get("/admin/settings");
      const nextSettings = { ...DEFAULT_RESULT_SETTINGS, ...(res.data || {}) };
      setResultSettings(nextSettings);
      const resetHour = clampInteger(nextSettings.market_result_day_reset_hour, 2, 0, 23);
      const nextResultDate = getBusinessResultDate(resetHour);
      setForm((current) => (
        current.result_date === defaultResultDateRef.current
          ? { ...current, result_date: nextResultDate }
          : current
      ));
    } catch {
      setResultSettings(DEFAULT_RESULT_SETTINGS);
    }
  };

  const showNotice = (type, title, message) => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice({ type, title, message });
    noticeTimerRef.current = setTimeout(() => setNotice(null), 4200);
  };

  const updateForm = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "pana") next.digit = calculateDigit(value);
      return next;
    });
  };

  useEffect(() => {
    if (!availableMarkets.length) {
      if (form.market_id) setForm((current) => ({ ...current, market_id: "" }));
      return;
    }

    if (!availableMarkets.some((market) => String(market.id) === String(form.market_id))) {
      setForm((current) => ({ ...current, market_id: String(availableMarkets[0].id) }));
    }
  }, [availableMarkets, form.market_id]);

  useEffect(() => {
    if (!selectedMarket) return;
    if (!openDeclared) {
      setForm((current) => current.session === "OPEN" ? current : ({ ...current, session: "OPEN", pana: "", digit: "" }));
      return;
    }
    if (openDeclared && !closeDeclared && form.session !== "CLOSE") {
      setForm((current) => ({ ...current, session: "CLOSE", pana: "", digit: "" }));
    }
  }, [closeDeclared, form.session, openDeclared, selectedMarket]);

  const previewWinners = async () => {
    if (!form.market_id || !form.result_date || !form.session || !form.pana || form.digit === "") {
      showNotice("error", "Missing Details", "Fill market, date, session, panna and digit before preview.");
      return;
    }
    try {
      const res = await API.post("/admin/results/preview", form);
      setPreview(res.data || null);
      setShowModal(true);
    } catch (err) {
      showNotice("error", "Preview Failed", err?.response?.data?.message || "Unable to preview winners");
    }
  };

  const declareResult = async () => {
    if (!form.market_id || !form.result_date || !form.session || !form.pana || form.digit === "") {
      showNotice("error", "Missing Details", "Fill market, date, session, panna and digit before declaring.");
      return;
    }

    setSaving(true);
    try {
      const res = await API.post("/admin/results/declare", form);
      setPreview({
        winners: res.data?.winners || 0,
        total_payout: res.data?.total_payout || 0,
        winner_rows: [],
      });
      await loadResults();
      setForm((current) => ({ ...current, pana: "", digit: "" }));
      showNotice(
        "success",
        "Result Declared",
        `${selectedMarket?.name || "Market"} ${String(form.session).toUpperCase()} ${form.pana}-${form.digit} saved.`
      );
    } catch (err) {
      showNotice("error", "Declare Failed", err?.response?.data?.message || "Unable to declare result");
    } finally {
      setSaving(false);
    }
  };

  const deleteResultSession = async (item, session) => {
    try {
      const safeDate = String(item.result_date || "").slice(0, 10);
      const normalizedSession = String(session || "").toUpperCase();
      const confirmed = window.confirm(
        `Delete ${normalizedSession} result for ${item.market_name} on ${formatDate(safeDate)}?\n\nWinning payouts for this session will be reversed and bet history will return to pending.`
      );
      if (!confirmed) return;

      const res = await API.delete(`/admin/results/${item.market_id}|${safeDate}|${normalizedSession}`);
      await loadResults();
      const rollback = res.data?.rollback;
      if (rollback) {
        showNotice(
          "success",
          "Result Deleted",
          `${normalizedSession} reversed ${rollback.reversed_bets || 0} winning bets / Rs ${Number(rollback.reversed_payout || 0).toFixed(2)}.`
        );
      }
    } catch (err) {
      showNotice("error", "Delete Failed", err?.response?.data?.message || "Unable to delete result");
    }
  };

  const markPending = async () => {
    if (!selectedMarket) return;
    try {
      await API.post("/admin/results/pending", {
        market_id: selectedMarket.id,
        result_date: form.result_date,
        session: form.session,
        remark: overdueState?.message || "Pending due result",
      });
      await loadResults();
      showNotice("success", "Pending Saved", "Pending flag saved for this result.");
    } catch (err) {
      showNotice("error", "Pending Failed", err?.response?.data?.message || "Unable to mark pending");
    }
  };

  const showDeclaredWinners = async (item, session) => {
    const normalizedSession = String(session || "").toUpperCase();
    const safeDate = String(item.result_date || "").slice(0, 10);
    try {
      const res = await API.get(
        `/admin/results/winners?market_id=${item.market_id}&result_date=${safeDate}&session=${normalizedSession}`
      );
      setPreview({
        winners: res.data?.winners || 0,
        total_payout: res.data?.total_payout || 0,
        winner_rows: res.data?.winner_rows || [],
      });
      setShowModal(true);
    } catch (err) {
      showNotice("error", "Winners Failed", err?.response?.data?.message || "Unable to load winners");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    declareResult();
  };

  return (
    <AdminLayout>
      <div style={page}>
        {notice ? (
          <div style={{ ...noticeToast, ...(noticeTone[notice.type] || noticeTone.info) }} role="status">
            <div>
              <strong style={noticeTitle}>{notice.title}</strong>
              <div style={noticeText}>{notice.message}</div>
            </div>
            <button type="button" style={noticeClose} onClick={() => setNotice(null)}>
              <X size={16} />
            </button>
          </div>
        ) : null}

        <div style={pageHeader}>
          <div>
            <h2 style={title}>Market Outcome</h2>
            <p style={subtitle}>Declare results, calculate winners, send notifications</p>
          </div>
          <div style={headerActions}>
            <label style={smallDateControl} title="Change result date">
              <CalendarDays size={17} />
              <input type="date" value={form.result_date} onChange={(e) => updateForm("result_date", e.target.value)} style={smallDateInput} />
            </label>
            <button type="button" style={refreshButton} onClick={async () => { await loadValidPannas(); await loadMarkets(); await loadResults(); }}>
              Refresh
            </button>
            <span style={liveBadge}>Live</span>
          </div>
        </div>

        {upcomingResults.length ? (
          <section style={upcomingPanel}>
            <div>
              <h3 style={upcomingTitle}>Upcoming Results</h3>
              <p style={upcomingSubtitle}>Markets becoming available soon for safe result entry</p>
            </div>
            <div style={upcomingGrid}>
              {upcomingResults.map((item) => (
                <article key={`${item.market.id}-${item.session}`} style={upcomingCard}>
                  <span style={sessionChip(item.session)}>{item.session}</span>
                  <div>
                    <strong style={upcomingMarket}>{item.market.name}</strong>
                    <div style={upcomingMeta}>
                      {item.revealLabel ? `Appears ${item.revealLabel}` : "Appearing soon"} • Result {item.resultLabel}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <section style={card}>
          <h3 style={sectionTitle}>Declare Result</h3>

          {selectedMarket && (
            <div style={selectedMarketBar} className="admin-outcome-selected">
              <div style={marketCell}>
                <span style={{ ...marketBadgeLarge, ...getMarketBadgeTone(selectedMarket.name) }}>
                  {getInitials(selectedMarket.name)}
                </span>
                <div>
                  <div style={selectedMarketName}>{selectedMarket.name}</div>
                  <div style={selectedMarketMeta}>
                    {(() => {
                      const currentWindow = getEffectiveMarketWindow(selectedMarket, form.result_date);
                      return `${formatTime(currentWindow.open_time || selectedMarket.open_time)} - ${formatTime(currentWindow.close_time || selectedMarket.close_time)}`;
                    })()}
                  </div>
                </div>
              </div>
              <span style={sessionChip(form.session)}>{form.session}</span>
            </div>
          )}

          {selectedMarket && (overdueState || pendingRow) ? (
            <div style={pendingAlertCard}>
              <div>
                <strong style={pendingAlertTitle}>{pendingRow ? "Pending Result Marked" : "Result Delay Alert"}</strong>
                <div style={pendingAlertText}>
                  {pendingRow
                    ? `${String(pendingRow.session || "").toUpperCase()} pending by ${pendingRow.marked_by_name || "Admin"}`
                    : overdueState?.message}
                </div>
              </div>
              {!pendingRow && overdueState ? (
                <button type="button" style={pendingActionButton} onClick={markPending}>Mark Pending</button>
              ) : null}
            </div>
          ) : null}

          <form onSubmit={handleSubmit}>
            <div style={formGrid} className="admin-outcome-form">
              <label style={field}>
                <span>Game</span>
                <div style={dropdownWrap} ref={marketMenuRef}>
                  <button type="button" style={dropdownButton} onClick={() => setMarketMenuOpen((value) => !value)}>
                    {selectedMarket ? (
                      <div style={marketCell}>
                        <span style={{ ...marketBadge, ...getMarketBadgeTone(selectedMarket.name) }}>{getInitials(selectedMarket.name)}</span>
                        <div>
                          <div style={marketName}>{selectedMarket.name}</div>
                          <div style={marketTime}>{(() => {
                            const currentWindow = getEffectiveMarketWindow(selectedMarket, form.result_date);
                            return `${formatTime(currentWindow.open_time || selectedMarket.open_time)} - ${formatTime(currentWindow.close_time || selectedMarket.close_time)}`;
                          })()}</div>
                        </div>
                      </div>
                    ) : (
                      <span style={placeholderText}>Select market</span>
                    )}
                    <ChevronDown size={18} color="#64748b" />
                  </button>

                  {marketMenuOpen && (
                    <div style={dropdownMenu}>
                      {availableMarkets.map((market) => (
                        <button
                          key={market.id}
                          type="button"
                          style={dropdownItem(String(market.id) === String(form.market_id))}
                          onClick={() => {
                            updateForm("market_id", String(market.id));
                            setMarketMenuOpen(false);
                          }}
                        >
                          <span style={{ ...marketBadge, ...getMarketBadgeTone(market.name) }}>{getInitials(market.name)}</span>
                          <div>
                            <div style={marketName}>{market.name}</div>
                            <div style={marketTime}>{(() => {
                              const currentWindow = getEffectiveMarketWindow(market, form.result_date);
                              return `${formatTime(currentWindow.open_time || market.open_time)} - ${formatTime(currentWindow.close_time || market.close_time)}`;
                            })()}</div>
                          </div>
                        </button>
                      ))}
                      {!availableMarkets.length ? (
                        <div style={emptyMarket}>
                          Open markets appear {openRevealMinutes} minutes before open time. Close markets appear {closeRevealMinutes} minutes before close time.
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </label>

              <label style={field}>
                <span>Session</span>
                <div style={!openDeclared || closeDeclared ? sessionButtonRowSingle : sessionButtonRow}>
                  {!openDeclared ? (
                    <button
                      type="button"
                      style={form.session === "OPEN" ? openSessionActive : openSessionButton}
                      onClick={() => updateForm("session", "OPEN")}
                    >
                      OPEN
                    </button>
                  ) : null}
                  {openDeclared && !closeDeclared ? (
                    <button
                      type="button"
                      style={form.session === "CLOSE" ? closeSessionActive : closeSessionButton}
                      onClick={() => updateForm("session", "CLOSE")}
                    >
                      CLOSE
                    </button>
                  ) : null}
                  {openDeclared && closeDeclared ? (
                    <button type="button" style={disabledSessionButton} disabled>
                      RESULT DONE
                    </button>
                  ) : null}
                </div>
              </label>

              <label style={field}>
                <span>Pana</span>
                <div style={panaWrap} ref={panaMenuRef}>
                  <div style={panaInputShell}>
                    <input
                      value={form.pana}
                      autoComplete="off"
                      inputMode="numeric"
                      enterKeyHint="done"
                      onFocus={() => setPanaMenuOpen(true)}
                      onChange={(e) => updateForm("pana", e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
                      placeholder="Type or select panna"
                      style={panaInlineInput}
                    />
                    <button type="button" style={panaArrowButton} onClick={() => setPanaMenuOpen((value) => !value)}>
                      <ChevronDown size={18} color="#64748b" />
                    </button>
                  </div>
                  {panaMenuOpen && (
                    <div style={panaMenu}>
                      <div style={panaSearchBox}>
                        <input
                          value={form.pana}
                          autoComplete="off"
                          inputMode="numeric"
                          enterKeyHint="done"
                          onChange={(e) => updateForm("pana", e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
                          placeholder="Search Pana"
                          style={panaSearchInput}
                        />
                      </div>
                      <div style={panaList}>
                        {panaSuggestions.map((pana) => (
                          <button
                            key={pana}
                            type="button"
                            style={panaItem}
                            onClick={() => {
                              updateForm("pana", pana);
                              setPanaMenuOpen(false);
                            }}
                          >
                            <strong>{pana}</strong>
                            <span style={panaDigitBadge}>{`-> ${calculateDigit(pana)}`}</span>
                          </button>
                        ))}
                        {!panaSuggestions.length && <div style={emptyPana}>No valid panna found</div>}
                      </div>
                    </div>
                  )}
                </div>
              </label>

              <label style={digitField}>
                <span>Digit</span>
                <input
                  value={form.digit}
                  onChange={(e) => updateForm("digit", e.target.value.replace(/[^\d]/g, "").slice(0, 1))}
                  placeholder="Auto"
                  style={digitInput}
                />
              </label>
            </div>

            <div style={selectionPreview}>
              <div style={selectionBox}>
                <span style={selectionLabel}>PANA</span>
                <strong style={selectionPana}>{form.pana || "---"}</strong>
              </div>
              <div style={selectionBox}>
                <span style={selectionLabel}>DIGIT</span>
                <strong style={selectionDigit}>{form.digit || "-"}</strong>
              </div>
            </div>

            <div style={buttonRow} className="admin-mobile-actions">
              <button type="button" style={form.pana ? previewButtonActive : previewButton} onClick={previewWinners} disabled={!selectedMarket}>
                <Eye size={18} /> Show Winners
              </button>
              <button type="submit" style={form.pana && form.digit ? submitButtonActive : submitButton} disabled={saving || !selectedMarket}>
                <Save size={18} /> {saving ? "Saving..." : "Declare Result"}
              </button>
            </div>
          </form>
        </section>

        <section style={card}>
          <div style={tableTitleRow}>
            <h3 style={sectionTitle}>Declared Results</h3>
            <span style={countText}>{sortedResults.length} results</span>
          </div>

          <table style={table}>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={th}>Game Name</th>
                <th style={th}>Result Date</th>
                <th style={th}>Open Pana</th>
                <th style={th}>Close Pana</th>
                <th style={th}>Declared Time</th>
                <th style={th}>Winners</th>
                <th style={th}>Total Win</th>
                <th style={th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((item, index) => (
                <tr key={item.id} style={tableRow}>
                  <td style={td}>{index + 1}</td>
                  <td style={td}>
                    <div style={marketCell}>
                      <span style={{ ...marketBadge, ...getMarketBadgeTone(item.market_name) }}>{getInitials(item.market_name)}</span>
                      <div>
                        <div style={marketName}>{item.market_name}</div>
                        <div style={marketTime}>{(() => {
                          const currentWindow = getEffectiveMarketWindow(item, item.result_date);
                          return `${formatTime(currentWindow.open_time || item.open_time)} - ${formatTime(currentWindow.close_time || item.close_time)}`;
                        })()}</div>
                      </div>
                    </div>
                  </td>
                  <td style={td}>{formatDate(item.result_date)}</td>
                  <td style={{ ...td, ...resultCellOpen }}>{item.open_pana ? `${item.open_pana}-${item.open_digit}` : "***"}</td>
                  <td style={{ ...td, ...resultCellClose }}>{item.close_pana ? `${item.close_pana}-${item.close_digit}` : "***"}</td>
                  <td style={td}>
                    <div>{formatDateTime(item.close_created_at || item.open_created_at)}</div>
                    <small style={declaredByText}>
                      {item.close_declared_by_name || item.open_declared_by_name
                        ? `By ${item.close_declared_by_name || item.open_declared_by_name}`
                        : "-"}
                    </small>
                  </td>
                  <td style={td}>
                    <div style={winnerActionStack}>
                      {item.open_pana ? (
                        <button type="button" style={winnerMiniButton} onClick={() => showDeclaredWinners(item, "OPEN")}>
                          Open: {Number(item.open_winners || 0)}
                        </button>
                      ) : null}
                      {item.close_pana ? (
                        <button type="button" style={winnerMiniButtonClose} onClick={() => showDeclaredWinners(item, "CLOSE")}>
                          Close: {Number(item.close_winners || 0)}
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td style={td}>
                    <div style={winnerActionStack}>
                      {item.open_pana ? (
                        <button type="button" style={payoutMiniButton} onClick={() => showDeclaredWinners(item, "OPEN")}>
                          Open Rs {Number(item.open_total_payout || 0).toFixed(2)}
                        </button>
                      ) : null}
                      {item.close_pana ? (
                        <button type="button" style={payoutMiniButton} onClick={() => showDeclaredWinners(item, "CLOSE")}>
                          Close Rs {Number(item.close_total_payout || 0).toFixed(2)}
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td style={td}>
                    <div style={resultActionGroup}>
                      {item.open_pana ? (
                        <button type="button" style={deleteOpenButton} onClick={() => deleteResultSession(item, "OPEN")}>
                          Delete OPEN
                        </button>
                      ) : null}
                      {item.close_pana ? (
                        <button type="button" style={deleteButton} onClick={() => deleteResultSession(item, "CLOSE")}>
                          Delete CLOSE
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {showModal && preview && (
          <div style={modalBackdrop} className="admin-modal-backdrop" onClick={() => setShowModal(false)}>
            <div style={modalCard} className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
              <div style={modalHeader}>
                <div>
                  <div style={modalTitle}>Winners Preview</div>
                  <div style={modalSubtitle}>{`${preview.winners || 0} winners - Rs ${Number(preview.total_payout || 0).toFixed(2)} total`}</div>
                </div>
                <button type="button" style={closeIcon} onClick={() => setShowModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div style={modalBody} className="admin-modal-body">
                <table style={table}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Game</th>
                      <th>Number</th>
                      <th>Bid</th>
                      <th>Win</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.winner_rows || []).map((row, index) => (
                      <tr key={`${row.user_id}-${index}`}>
                        <td>
                          <div style={winnerUserCell}>
                            <AdminUserLink userId={row.user_id} name={row.user_name} phone={row.user_phone} />
                          </div>
                        </td>
                        <td>{row.game}</td>
                        <td style={valueStrong}>{row.number}</td>
                        <td>{`Rs ${Number(row.bid_amount || 0).toFixed(2)}`}</td>
                        <td style={winValue}>{`Rs ${Number(row.win_amount || 0).toFixed(2)}`}</td>
                      </tr>
                    ))}
                    {!preview.winner_rows?.length && (
                      <tr>
                        <td colSpan={5} style={emptyRow}>No winners for this result.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={modalFooter}>
                <button type="button" style={closeButton} onClick={() => setShowModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const calculateDigit = (pana) => {
  const digits = String(pana || "").replace(/\D/g, "").slice(0, 3).split("").map(Number);
  if (!digits.length) return "";
  return String(digits.reduce((sum, digit) => sum + digit, 0) % 10);
};

const getPanaSuggestions = (query, validPannas = []) => {
  const clean = String(query || "").replace(/\D/g, "").slice(0, 3);
  return Array.from(new Set(validPannas))
    .filter((pana) => !clean || String(pana).includes(clean))
    .sort((a, b) => String(a).localeCompare(String(b)));
};

const getBusinessResultDate = (resetHour = 2) => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const map = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
  const base = new Date(Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day)));
  const hour = Number(map.hour) === 24 ? 0 : Number(map.hour);
  if (hour < clampInteger(resetHour, 2, 0, 23)) {
    base.setUTCDate(base.getUTCDate() - 1);
  }
  return base.toISOString().slice(0, 10);
};

const getDeclaredSessions = (rows, marketId) => {
  const row = rows.find((item) => String(item.market_id) === String(marketId));
  return {
    open: Boolean(row?.open_pana && row?.open_digit),
    close: Boolean(row?.close_pana && row?.close_digit),
  };
};

const isMarketAvailableForResult = (market, resultDate, rows, settings = {}) => {
  if (!market || market.is_active === 0) return false;
  const currentWindow = getEffectiveMarketWindow(market, resultDate);
  if (currentWindow.closed) return false;
  if (isNightMarketHiddenDuringDay(market, resultDate, settings.dayMarketCutoffHour)) return false;

  const declared = getDeclaredSessions(rows, market.id);
  if (!declared.open) {
    return getResultRevealState(market, resultDate, "OPEN", settings.openRevealMinutes).ready;
  }
  if (declared.close) return false;
  return getResultRevealState(market, resultDate, "CLOSE", settings.closeRevealMinutes).ready;
};

const getUpcomingResultItems = (markets = [], resultDate, rows = [], settings = {}) => {
  const upcomingWindowMinutes = clampInteger(settings.upcomingWindowMinutes, 30, 0, 1440);
  return markets
    .map((market) => {
      if (!market || market.is_active === 0) return null;
      const currentWindow = getEffectiveMarketWindow(market, resultDate);
      if (currentWindow.closed) return null;
      if (isNightMarketHiddenDuringDay(market, resultDate, settings.dayMarketCutoffHour)) return null;
      const declared = getDeclaredSessions(rows, market.id);
      const session = !declared.open ? "OPEN" : !declared.close ? "CLOSE" : "";
      if (!session) return null;
      const revealMinutes = session === "OPEN" ? settings.openRevealMinutes : settings.closeRevealMinutes;
      const state = getResultRevealState(market, resultDate, session, revealMinutes);
      if (state.ready || state.minutesUntilReveal < 0 || state.minutesUntilReveal > upcomingWindowMinutes) return null;
      return {
        market,
        session,
        minutesUntilReveal: state.minutesUntilReveal,
        revealLabel: state.revealLabel,
        resultLabel: state.resultLabel,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.minutesUntilReveal - b.minutesUntilReveal)
    .slice(0, 8);
};

const getResultRevealState = (market, resultDate, session = "OPEN", revealMinutes = 30, now = new Date()) => {
  if (!market || !resultDate) {
    return { ready: false, minutesUntilReveal: Number.POSITIVE_INFINITY, revealLabel: "", resultLabel: "" };
  }
  const currentWindow = getEffectiveMarketWindow(market, resultDate);
  const normalizedSession = String(session || "OPEN").toUpperCase();
  const sourceTime = normalizedSession === "CLOSE"
    ? (currentWindow.close_time || market.close_time)
    : (currentWindow.open_time || market.open_time);
  const sessionMinutesRaw = timeStringToMinutes(sourceTime);
  if (sessionMinutesRaw === null) {
    return { ready: true, minutesUntilReveal: 0, revealLabel: "", resultLabel: "" };
  }
  const openMinutes = timeStringToMinutes(currentWindow.open_time || market.open_time);
  let sessionMinutes = sessionMinutesRaw;
  if (normalizedSession === "CLOSE" && openMinutes !== null && sessionMinutes <= openMinutes) {
    sessionMinutes += 1440;
  }

  const resultDayNumber = dateKeyToDayNumber(resultDate);
  const nowParts = getIndiaDateTimeParts(now);
  const nowDayNumber = dateKeyToDayNumber(nowParts.date);
  if (resultDayNumber === null || nowDayNumber === null) {
    return { ready: false, minutesUntilReveal: Number.POSITIVE_INFINITY, revealLabel: "", resultLabel: formatMinutesToTimeLabel(sessionMinutes) };
  }

  const currentMinutesFromResultDate = ((nowDayNumber - resultDayNumber) * 1440) + nowParts.hour * 60 + nowParts.minute;
  const revealAtMinutes = sessionMinutes - clampInteger(revealMinutes, normalizedSession === "OPEN" ? 30 : 10, 0, 1440);
  return {
    ready: currentMinutesFromResultDate >= revealAtMinutes,
    minutesUntilReveal: revealAtMinutes - currentMinutesFromResultDate,
    revealLabel: formatMinutesToTimeLabel(revealAtMinutes),
    resultLabel: formatMinutesToTimeLabel(sessionMinutes),
  };
};

const isNightMarketHiddenDuringDay = (market, resultDate, cutoffHour = 18, now = new Date()) => {
  const cutoffMinutes = clampInteger(cutoffHour, 18, 0, 23) * 60;
  const nowParts = getIndiaDateTimeParts(now);
  if (String(resultDate || "").slice(0, 10) !== nowParts.date) return false;
  const currentMinutes = nowParts.hour * 60 + nowParts.minute;
  if (currentMinutes >= cutoffMinutes) return false;
  const currentWindow = getEffectiveMarketWindow(market, resultDate);
  const closeMinutes = timeStringToMinutes(currentWindow.close_time || market.close_time);
  const openMinutes = timeStringToMinutes(currentWindow.open_time || market.open_time);
  if (closeMinutes === null) return false;
  if (openMinutes !== null && closeMinutes <= openMinutes) return true;
  return closeMinutes > cutoffMinutes;
};

const sortDeclaredResults = (rows = []) => (
  [...rows].sort((a, b) => {
    const timeCompare = getLatestDeclaredTime(b) - getLatestDeclaredTime(a);
    if (timeCompare !== 0) return timeCompare;
    const dateCompare = String(b.result_date || "").localeCompare(String(a.result_date || ""));
    if (dateCompare !== 0) return dateCompare;
    return String(a.market_name || "").localeCompare(String(b.market_name || ""));
  })
);

const getLatestDeclaredTime = (item = {}) => {
  const closeTime = Date.parse(item.close_created_at || "");
  const openTime = Date.parse(item.open_created_at || "");
  return Math.max(Number.isFinite(closeTime) ? closeTime : 0, Number.isFinite(openTime) ? openTime : 0);
};

const getIndiaDateTimeParts = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const map = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
  const hour = Number(map.hour) === 24 ? 0 : Number(map.hour || 0);
  return {
    date: `${map.year}-${map.month}-${map.day}`,
    hour,
    minute: Number(map.minute || 0),
  };
};

const dateKeyToDayNumber = (value) => {
  const match = String(value || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return Math.floor(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / 86400000);
};

const clampInteger = (value, fallback, min, max) => {
  const parsed = Number.parseInt(value, 10);
  const safe = Number.isFinite(parsed) ? parsed : fallback;
  return Math.min(max, Math.max(min, safe));
};

const formatDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value) => {
  if (!value) return "--:--";
  const [hourRaw, minute = "00"] = String(value).split(":");
  const hour = Number(hourRaw);
  const suffix = hour >= 12 ? "pm" : "am";
  const displayHour = ((hour + 11) % 12) + 1;
  return `${String(displayHour).padStart(2, "0")}:${minute} ${suffix}`;
};

const getOverdueState = (market, resultDate, session, declaredSessions) => {
  if (!market || !resultDate) return null;
  const normalizedSession = String(session || "OPEN").toUpperCase();
  if (normalizedSession === "OPEN" && declaredSessions.open) return null;
  if (normalizedSession === "CLOSE" && declaredSessions.close) return null;
  const currentWindow = getEffectiveMarketWindow(market, resultDate);
  const sourceTime = normalizedSession === "OPEN"
    ? (currentWindow.open_time || market.open_time)
    : (currentWindow.close_time || market.close_time);
  const minutes = timeStringToMinutes(sourceTime);
  if (minutes === null) return null;
  const [year, month, day] = String(resultDate).split("-").map(Number);
  const deadline = new Date(year, (month || 1) - 1, day || 1, Math.floor(minutes / 60), minutes % 60, 0, 0);
  const now = new Date();
  const diffMinutes = Math.floor((now.getTime() - deadline.getTime()) / 60000);
  if (diffMinutes < 30) return null;
  return {
    diffMinutes,
    message: `${normalizedSession} result is overdue by ${diffMinutes} minutes from session time.`,
  };
};

const timeStringToMinutes = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

const formatMinutesToTimeLabel = (minutes) => {
  if (!Number.isFinite(minutes)) return "";
  const normalized = ((Math.floor(minutes) % 1440) + 1440) % 1440;
  const hour = Math.floor(normalized / 60);
  const minute = normalized % 60;
  return formatTime(`${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
};

const getInitials = (name = "") =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "MK";

const getMarketBadgeTone = (name = "") => {
  const key = String(name || "").toUpperCase();
  if (key.includes("SRIDEVI")) return { background: "linear-gradient(135deg, #22c55e, #16a34a)" };
  if (key.includes("MILAN")) return { background: "linear-gradient(135deg, #7c3aed, #a855f7)" };
  if (key.includes("TIME")) return { background: "linear-gradient(135deg, #f97316, #ea580c)" };
  if (key.includes("MADHUR")) return { background: "linear-gradient(135deg, #ec4899, #db2777)" };
  if (key.includes("KALYAN")) return { background: "linear-gradient(135deg, #ef4444, #f97316)" };
  if (key.includes("RAJDHANI")) return { background: "linear-gradient(135deg, #0ea5e9, #2563eb)" };
  if (key.includes("MAIN")) return { background: "linear-gradient(135deg, #06b6d4, #0891b2)" };
  if (key.includes("SADAR")) return { background: "linear-gradient(135deg, #0284c7, #0f766e)" };
  return {};
};

const getEffectiveMarketWindow = (market, resultDate = "") => {
  if (!market) return { open_time: "", close_time: "", closed: false };
  const source = resultDate ? new Date(`${resultDate}T12:00:00+05:30`) : new Date();
  return getMarketWindowForNow(market, source);
};

const sessionChip = (value = "OPEN") => ({
  minWidth: "92px",
  height: "38px",
  borderRadius: "999px",
  display: "grid",
  placeItems: "center",
  fontWeight: 800,
  fontSize: "12px",
  background: value === "OPEN" ? "#dcfce7" : "#fee2e2",
  color: value === "OPEN" ? "#15803d" : "#dc2626",
});

const page = { display: "grid", gap: "22px" };
const noticeToast = {
  position: "fixed",
  top: "84px",
  right: "22px",
  width: "min(420px, calc(100vw - 28px))",
  zIndex: 1200,
  borderRadius: "18px",
  padding: "15px 16px",
  boxShadow: "0 22px 50px rgba(15, 23, 42, 0.20)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "14px",
};
const noticeTone = {
  success: { background: "#ecfdf5", border: "1px solid #86efac", color: "#14532d" },
  error: { background: "#fef2f2", border: "1px solid #fecaca", color: "#7f1d1d" },
  info: { background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e3a8a" },
};
const noticeTitle = { display: "block", fontSize: "14px", fontWeight: 900 };
const noticeText = { marginTop: "5px", fontSize: "13px", lineHeight: 1.35, fontWeight: 700 };
const noticeClose = { width: "28px", height: "28px", border: 0, borderRadius: "999px", background: "rgba(15,23,42,0.08)", color: "inherit", display: "grid", placeItems: "center", cursor: "pointer", flex: "0 0 auto" };
const pageHeader = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const headerActions = { display: "flex", alignItems: "center", gap: "12px" };
const title = { margin: 0, fontSize: "26px", fontWeight: 800 };
const subtitle = { margin: "6px 0 0", color: "#64748b" };
const liveBadge = { padding: "10px 18px", borderRadius: "0", background: "#ecfdf5", color: "#059669", fontWeight: 700, border: "1px solid #bbf7d0" };
const refreshButton = { height: "42px", padding: "0 16px", borderRadius: "12px", border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", fontWeight: 700 };
const smallDateControl = { height: "42px", minWidth: "46px", border: "1px solid #dbeafe", borderRadius: "12px", background: "#fff", color: "#2563eb", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "0 10px", cursor: "pointer" };
const smallDateInput = { width: "112px", border: 0, outline: 0, background: "transparent", color: "#1d4ed8", fontSize: "12px", fontWeight: 900, cursor: "pointer" };
const upcomingPanel = { background: "#fff", border: "1px solid #bfdbfe", borderRadius: "18px", padding: "16px", display: "grid", gridTemplateColumns: "1fr", gap: "14px", alignItems: "start", boxShadow: "0 10px 24px rgba(37,99,235,0.06)" };
const upcomingTitle = { margin: 0, fontSize: "16px", fontWeight: 900, color: "#1e3a8a" };
const upcomingSubtitle = { margin: "5px 0 0", color: "#64748b", fontSize: "12px", fontWeight: 700 };
const upcomingGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "10px" };
const upcomingCard = { minHeight: "58px", border: "1px solid #dbeafe", borderRadius: "14px", background: "#eff6ff", padding: "10px", display: "flex", alignItems: "center", gap: "10px" };
const upcomingMarket = { display: "block", color: "#0f172a", fontSize: "13px", fontWeight: 900 };
const upcomingMeta = { marginTop: "3px", color: "#475569", fontSize: "11px", fontWeight: 800, lineHeight: 1.25 };
const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "22px", padding: "28px", boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)" };
const sectionTitle = { margin: 0, fontSize: "18px", fontWeight: 800 };
const selectedMarketBar = {
  marginTop: "18px",
  marginBottom: "8px",
  padding: "16px 18px",
  borderRadius: "18px",
  border: "1px solid #dbe4f0",
  background: "#f8fbff",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px"
};
const selectedMarketName = { fontWeight: 800, fontSize: "18px", color: "#0f172a" };
const selectedMarketMeta = { marginTop: "4px", color: "#64748b", fontSize: "13px", fontWeight: 700 };
const pendingAlertCard = { marginTop: "14px", marginBottom: "4px", padding: "14px 16px", borderRadius: "16px", border: "1px solid #fed7aa", background: "linear-gradient(135deg,#fff7ed,#fff1f2)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" };
const pendingAlertTitle = { color: "#9a3412", fontSize: "14px", fontWeight: 900 };
const pendingAlertText = { marginTop: "4px", color: "#7c2d12", fontSize: "12px", fontWeight: 700 };
const pendingActionButton = { minWidth: "128px", height: "42px", border: 0, borderRadius: "12px", background: "#f97316", color: "#fff", fontWeight: 800, cursor: "pointer" };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: "16px", marginTop: "24px", alignItems: "end" };
const field = { display: "grid", gap: "8px", color: "#334155", fontWeight: 700 };
const input = { height: "58px", border: "1px solid #cbd5e1", borderRadius: "0", padding: "0 14px", fontSize: "16px", background: "#fff" };
const digitField = { ...field, maxWidth: "88px" };
const digitInput = { ...input, width: "72px", boxSizing: "border-box", textAlign: "center", padding: 0, fontWeight: 900 };
const dropdownWrap = { position: "relative" };
const dropdownButton = {
  width: "100%",
  minHeight: "58px",
  border: "1px solid #cbd5e1",
  borderRadius: "0",
  padding: "10px 14px",
  fontSize: "16px",
  background: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  cursor: "pointer",
  textAlign: "left"
};
const placeholderText = { color: "#94a3b8", fontWeight: 500 };
const dropdownMenu = {
  position: "absolute",
  top: "calc(100% + 6px)",
  left: 0,
  right: 0,
  maxHeight: "280px",
  overflowY: "auto",
  background: "#fff",
  border: "1px solid #dbe4f0",
  boxShadow: "0 18px 40px rgba(15,23,42,0.14)",
  zIndex: 20
};
const emptyMarket = { padding: "16px", color: "#64748b", fontSize: "13px", fontWeight: 800, lineHeight: 1.4 };
const dropdownItem = (active) => ({
  width: "100%",
  border: 0,
  borderBottom: "1px solid #eef2f7",
  background: active ? "#eff6ff" : "#fff",
  padding: "12px 14px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  cursor: "pointer",
  textAlign: "left"
});
const sessionButtonRow = { height: "58px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" };
const sessionButtonRowSingle = { height: "58px", display: "grid", gridTemplateColumns: "1fr", gap: "8px" };
const openSessionButton = { border: "1px solid #bbf7d0", background: "#ecfdf5", color: "#047857", fontWeight: 900, fontSize: "15px", cursor: "pointer" };
const closeSessionButton = { border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", fontWeight: 900, fontSize: "15px", cursor: "pointer" };
const openSessionActive = { ...openSessionButton, background: "#16a34a", borderColor: "#16a34a", color: "#fff", boxShadow: "0 8px 18px rgba(22,163,74,0.22)" };
const closeSessionActive = { ...closeSessionButton, background: "#dc2626", borderColor: "#dc2626", color: "#fff", boxShadow: "0 8px 18px rgba(220,38,38,0.22)" };
const disabledSessionButton = { border: "1px solid #e5e7eb", background: "#f1f5f9", color: "#94a3b8", fontWeight: 900, fontSize: "13px", cursor: "not-allowed" };
const panaWrap = { position: "relative" };
const panaInputShell = { width: "100%", height: "58px", border: "1px solid #cbd5e1", background: "#fff", display: "flex", alignItems: "center", overflow: "hidden" };
const panaInlineInput = { flex: 1, minWidth: 0, height: "100%", border: 0, outline: 0, padding: "0 14px", fontSize: "16px", fontWeight: 800, color: "#0f172a", background: "transparent" };
const panaArrowButton = { width: "48px", height: "100%", border: 0, borderLeft: "1px solid #e2e8f0", background: "#f8fafc", display: "grid", placeItems: "center", cursor: "pointer" };
const panaMenu = { position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#fff", border: "1px solid #bfdbfe", boxShadow: "0 18px 40px rgba(15,23,42,0.16)", zIndex: 30 };
const panaSearchBox = { padding: "8px", borderBottom: "1px solid #e5e7eb", background: "#eff6ff" };
const panaSearchInput = { width: "100%", height: "44px", border: "1px solid #93c5fd", outline: "2px solid #3b82f6", padding: "0 12px", fontSize: "16px", boxSizing: "border-box" };
const panaList = { maxHeight: "250px", overflowY: "auto", display: "grid" };
const panaItem = { minHeight: "40px", border: 0, borderBottom: "1px solid #f1f5f9", background: "#fff", padding: "0 14px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: "15px" };
const panaDigitBadge = { minWidth: "42px", height: "24px", borderRadius: "7px", background: "#eff6ff", color: "#2563eb", fontWeight: 900, display: "grid", placeItems: "center" };
const emptyPana = { minHeight: "44px", display: "grid", placeItems: "center", color: "#dc2626", fontSize: "13px", fontWeight: 800 };
const buttonRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "24px" };
const selectionPreview = { marginTop: "12px", border: "1px solid #e2e8f0", borderRadius: "14px", background: "#fff", display: "grid", gridTemplateColumns: "1fr 1fr", overflow: "hidden" };
const selectionBox = { minHeight: "68px", display: "grid", placeItems: "center", alignContent: "center", gap: "4px" };
const selectionLabel = { color: "#475569", fontSize: "11px", fontWeight: 800 };
const selectionPana = { fontSize: "34px", lineHeight: 1, color: "#316ce8", fontWeight: 900 };
const selectionDigit = { fontSize: "34px", lineHeight: 1, color: "#16a34a", fontWeight: 900 };
const previewButton = { height: "56px", border: 0, borderRadius: "18px", background: "#fed17f", color: "#fff", fontWeight: 800, fontSize: "16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" };
const submitButton = { height: "56px", border: 0, borderRadius: "18px", background: "#88a5ee", color: "#fff", fontWeight: 800, fontSize: "16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" };
const previewButtonActive = { ...previewButton, background: "linear-gradient(135deg, #f59e0b, #f97316)", boxShadow: "0 12px 24px rgba(249,115,22,.22)" };
const submitButtonActive = { ...submitButton, background: "linear-gradient(135deg, #16a34a, #2563eb)", boxShadow: "0 12px 24px rgba(37,99,235,.20)" };
const tableTitleRow = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" };
const countText = { color: "#64748b", fontWeight: 600 };
const table = { width: "100%", borderCollapse: "collapse" };
const th = { textAlign: "left", height: "44px", padding: "0 10px 14px", fontSize: "12px", color: "#64748b", borderBottom: "1px solid #e5e7eb" };
const td = { padding: "18px 10px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", fontSize: "14px", verticalAlign: "middle" };
const tableRow = { background: "#fff" };
const marketCell = { display: "flex", alignItems: "center", gap: "12px" };
const marketBadge = { width: "38px", height: "38px", display: "grid", placeItems: "center", background: "linear-gradient(90deg, #2563eb, #4f46e5)", color: "#fff", fontWeight: 800 };
const marketBadgeLarge = { width: "46px", height: "46px", borderRadius: "12px", display: "grid", placeItems: "center", background: "linear-gradient(90deg, #2563eb, #4f46e5)", color: "#fff", fontWeight: 800 };
const marketName = { fontWeight: 700, color: "#0f172a" };
const marketTime = { marginTop: "4px", fontSize: "12px", color: "#94a3b8" };
const valueStrong = { fontWeight: 800, color: "#0f172a" };
const resultCellOpen = { fontWeight: 800, color: "#15803d" };
const resultCellClose = { fontWeight: 800, color: "#dc2626" };
const resultActionGroup = { display: "flex", flexWrap: "wrap", gap: "8px" };
const winnerActionStack = { display: "grid", gap: "6px", alignItems: "start" };
const winnerMiniButton = { border: "1px solid #bbf7d0", borderRadius: "999px", background: "#f0fdf4", color: "#15803d", padding: "7px 10px", fontWeight: 900, cursor: "pointer", whiteSpace: "nowrap" };
const winnerMiniButtonClose = { ...winnerMiniButton, borderColor: "#fed7aa", background: "#fff7ed", color: "#c2410c" };
const payoutMiniButton = { border: "1px solid #bfdbfe", borderRadius: "999px", background: "#eff6ff", color: "#1d4ed8", padding: "7px 10px", fontWeight: 900, cursor: "pointer", whiteSpace: "nowrap" };
const deleteOpenButton = { border: 0, borderRadius: "10px", background: "#f97316", color: "#fff", padding: "10px 13px", fontWeight: 800, cursor: "pointer" };
const deleteButton = { border: 0, borderRadius: "10px", background: "#ef4444", color: "#fff", padding: "10px 16px", fontWeight: 700, cursor: "pointer" };
const modalBackdrop = { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)", display: "grid", placeItems: "center", zIndex: 1000, padding: "24px" };
const modalCard = { width: "100%", maxWidth: "920px", background: "#fff", borderRadius: "24px", overflow: "hidden", boxShadow: "0 24px 60px rgba(15, 23, 42, 0.24)" };
const modalHeader = { background: "linear-gradient(90deg, #f59e0b, #f97316)", color: "#fff", padding: "22px 26px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const modalTitle = { fontSize: "18px", fontWeight: 800 };
const modalSubtitle = { marginTop: "6px", opacity: 0.95 };
const closeIcon = { width: "36px", height: "36px", border: 0, background: "transparent", color: "#fff", cursor: "pointer" };
const modalBody = { padding: "18px 22px 0" };
const modalFooter = { padding: "20px 22px 22px", display: "flex", justifyContent: "flex-end" };
const closeButton = { minWidth: "120px", height: "48px", border: 0, borderRadius: "0", background: "#316ce8", color: "#fff", fontWeight: 800, cursor: "pointer" };
const winnerUserCell = { display: "grid", gap: "4px" };
const winValue = { color: "#16a34a", fontWeight: 800 };
const emptyRow = { textAlign: "center", padding: "24px", color: "#64748b" };
const declaredByText = { color: "#64748b", fontSize: "12px" };

export default AdminResults;
